import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DeviceMobile, X, Export, CheckCircle, Bell } from '@phosphor-icons/react';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { trackPwa } from '@/lib/pwaTrack';
import {
  isIos,
  isStandalone,
  pushSupported,
  getPushState,
  subscribeToPush,
  unsubscribeFromPush,
} from '@/lib/pwa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PwaInstall = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [installable, setInstallable] = useState(
    typeof window !== 'undefined' && !!window.__deferredInstallPrompt
  );
  const [pushState, setPushState] = useState('default'); // default|subscribed|denied|unsupported
  const [busy, setBusy] = useState(false);

  useBodyScrollLock(open);

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => {
      setInstalled(true);
      setInstallable(false);
    };
    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('pwa-installed', onInstalled);
    getPushState().then(setPushState).catch(() => setPushState('unsupported'));
    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  // This modal is a separate surface from PwaInstallBanner (it opens from the
  // header icon), so its events carry surface:'modal' to keep the two funnels
  // apart while sharing the banner's event names.
  const handleInstall = async () => {
    const dp = window.__deferredInstallPrompt;
    if (!dp) return;
    trackPwa('pwa_banner_install_click', { surface: 'modal', install_method: 'native_prompt' });
    dp.prompt();
    try {
      const choice = await dp.userChoice;
      trackPwa(
        choice?.outcome === 'accepted'
          ? 'pwa_banner_install_accepted'
          : 'pwa_banner_install_dismissed',
        { surface: 'modal' }
      );
    } finally {
      window.__deferredInstallPrompt = null;
      setInstallable(false);
    }
  };

  const handleEnableNotifications = async () => {
    setBusy(true);
    trackPwa('push_enable_click', { surface: 'modal' });
    try {
      await subscribeToPush(API, axios);
      setPushState('subscribed');
      trackPwa('push_permission_granted', { surface: 'modal' });
      toast.success('Notifications enabled - you’ll hear about new fits for your height.');
    } catch (e) {
      if (e.message === 'denied') {
        trackPwa('push_permission_denied', { surface: 'modal' });
        toast.error('Notification permission was blocked.');
      } else if (e.message === 'server-disabled') {
        trackPwa('push_subscribe_failed', { surface: 'modal', error_message: 'server-disabled' });
        toast.error('Push is not configured on the server yet.');
      } else {
        trackPwa('push_subscribe_failed', { surface: 'modal', error_message: e?.message });
        toast.error('Could not enable notifications.');
      }
      setPushState(await getPushState().catch(() => 'default'));
    } finally {
      setBusy(false);
    }
  };

  const handleDisableNotifications = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush(API, axios);
      setPushState('default');
      // Opt-OUT is the one push signal with no event anywhere else, and it's
      // the number that tells us when the daily campaigns are wearing thin.
      trackPwa('push_disabled', { surface: 'modal' });
      toast('Notifications turned off.');
    } catch (e) {
      trackPwa('push_disable_failed', { surface: 'modal', error_message: e?.message });
      toast.error('Could not turn off notifications.');
    } finally {
      setBusy(false);
    }
  };

  const iosNeedsInstallForPush = isIos() && !isStandalone();

  return (
    <>
      <button
        onClick={() => {
          trackPwa('pwa_modal_open', {
            surface: 'modal',
            already_installed: installed,
            push_state: pushState,
          });
          setOpen(true);
        }}
        className="p-2 hover:bg-[#F0EFEB] rounded-md transition-colors"
        title="Install app & notifications"
        data-testid="pwa-install-button"
        aria-label="Install app and notifications"
      >
        <DeviceMobile size={22} weight="fill" className="text-[#2D302A]" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="pwa-overlay"
        >
          <div
            className="bg-white w-full max-w-md rounded-md border border-[#E4E1D9] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
            data-lenis-prevent
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-dialog"
          >
            <div className="flex justify-between items-start mb-6">
              <h2
                className="text-2xl tracking-tighter"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Get the app
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
                aria-label="Close"
                data-testid="pwa-close"
              >
                <X size={22} />
              </button>
            </div>

            {/* Install section */}
            <div className="mb-8">
              <p
                className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-3"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              >
                Install
              </p>

              {installed ? (
                <div className="flex items-center gap-2 text-[#2D302A]">
                  <CheckCircle size={20} weight="fill" className="text-green-700" />
                  <span className="text-sm">The app is installed on this device.</span>
                </div>
              ) : isIos() ? (
                <div className="text-sm text-[#4A4A48] space-y-2">
                  <p>To install on iPhone/iPad:</p>
                  <ol className="list-decimal list-inside space-y-1 text-[#6B6A68]">
                    <li className="flex items-center gap-1">
                      Tap the Share icon
                      <Export size={16} className="inline" />
                      in Safari's toolbar
                    </li>
                    <li>Scroll down and tap <b>Add to Home Screen</b></li>
                    <li>Open North of Six from your Home Screen</li>
                  </ol>
                </div>
              ) : installable ? (
                <button
                  onClick={handleInstall}
                  className="w-full bg-[#2D302A] text-white px-6 py-3 rounded-md hover:bg-[#1A1C18] transition-colors text-sm"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  data-testid="pwa-do-install"
                >
                  <span className="uppercase tracking-[0.15em] text-xs">Install app</span>
                </button>
              ) : (
                <p className="text-sm text-[#6B6A68]">
                  Open your browser menu and choose <b>Install</b> or <b>Add to Home Screen</b>.
                </p>
              )}
            </div>

            {/* Notifications section */}
            <div>
              <p
                className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-3"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
              >
                Notifications
              </p>

              {!pushSupported() ? (
                <p className="text-sm text-[#6B6A68]">
                  Your browser doesn't support notifications.
                </p>
              ) : !user ? (
                <p className="text-sm text-[#6B6A68]">Sign in to get alerts for new fits in your height.</p>
              ) : iosNeedsInstallForPush ? (
                <p className="text-sm text-[#6B6A68]">
                  On iPhone, install the app (above) and open it from your Home Screen, then enable
                  notifications here.
                </p>
              ) : pushState === 'denied' ? (
                <p className="text-sm text-[#6B6A68]">
                  Notifications are blocked. Enable them for this site in your browser settings.
                </p>
              ) : pushState === 'subscribed' ? (
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-sm text-[#2D302A]">
                    <Bell size={18} weight="fill" className="text-green-700" />
                    You'll be notified of new fits for your height.
                  </span>
                  <button
                    onClick={handleDisableNotifications}
                    disabled={busy}
                    className="text-xs text-[#6B6A68] hover:underline disabled:opacity-50"
                    data-testid="pwa-disable-notif"
                  >
                    Turn off
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleEnableNotifications}
                  disabled={busy}
                  className="w-full border border-[#2D302A] text-[#2D302A] px-6 py-3 rounded-md hover:bg-[#F0EFEB] transition-colors text-sm disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  data-testid="pwa-enable-notif"
                >
                  <span className="uppercase tracking-[0.15em] text-xs">
                    {busy ? 'Enabling…' : 'Enable notifications'}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PwaInstall;
